import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogIn, ArrowRight, Store, Shield } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
const buildRedirectUrl = () => window.location.origin + "/auth/callback";

export default function LoginPage() {
    const { user, status, persistSession } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("internal");
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();

    if (status === "authenticated" && user) {
        if (user.role === "SHOP_OWNER") {
            return <Navigate to="/merchant/home" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    const onEmailPassword = async ({ email, password }) => {
        try {
            const res = await api.post("/auth/login", { email, password });
            persistSession(res.data);
            toast.success(`Welcome back, ${res.data.user.full_name}!`);
            if (res.data.user.role === "SHOP_OWNER") {
                navigate("/merchant/home", { replace: true });
            } else {
                navigate("/dashboard", { replace: true });
            }
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const startGoogle = () => {
        const redirectUrl = buildRedirectUrl();
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="grid min-h-screen w-full grid-cols-1 lg:grid-cols-2">
            {/* Left hero */}
            <aside className="relative hidden overflow-hidden bg-neutral-950 p-10 text-white lg:block">
                <div className="absolute inset-0 opacity-30" aria-hidden>
                    <div className="absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-orange-500 blur-3xl" />
                    <div className="absolute -right-16 bottom-10 h-64 w-64 rounded-full bg-yellow-400 blur-3xl" />
                </div>
                <div className="relative z-10 flex h-full flex-col">
                    <Logo dark size={120} variant="lockup" />
                    <div className="mt-auto">
                        <h1 className="text-4xl font-extrabold leading-tight sm:text-5xl">
                            Kerala's{" "}
                            <span className="nest-gradient-text">Hyperlocal</span>
                            <br />
                            Offer Nest.
                        </h1>
                        <p className="mt-6 max-w-md text-lg text-neutral-300">
                            Deals, events, movies, exhibitions and job fairs —
                            curated across every district. Sign in to run your
                            shop dashboard or moderate the feed.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3 text-sm text-neutral-400">
                            <span className="rounded-full border border-white/15 px-3 py-1">
                                16 categories
                            </span>
                            <span className="rounded-full border border-white/15 px-3 py-1">
                                14 districts
                            </span>
                            <span className="rounded-full border border-white/15 px-3 py-1">
                                5 post types
                            </span>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Right form */}
            <section className="flex flex-col justify-center bg-white px-6 py-10 sm:px-10 lg:px-16">
                <div className="lg:hidden mb-8 flex justify-center">
                    <Logo size={88} variant="lockup" />
                </div>
                <div className="mx-auto w-full max-w-md">
                    <h2 className="text-2xl font-bold text-neutral-900">
                        Sign in to OfferNest
                    </h2>
                    <p className="mt-2 text-sm text-neutral-500">
                        Choose your login method below.
                    </p>

                    <Tabs value={tab} onValueChange={setTab} className="mt-6">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger
                                value="internal"
                                data-testid="tab-internal"
                                className="gap-2"
                            >
                                <Shield size={15} /> Team
                            </TabsTrigger>
                            <TabsTrigger
                                value="shop"
                                data-testid="tab-shop"
                                className="gap-2"
                            >
                                <Store size={15} /> Shop Owner
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="internal" className="mt-6 space-y-4">
                            <form
                                onSubmit={handleSubmit(onEmailPassword)}
                                className="space-y-4"
                                data-testid="login-form"
                            >
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@offernest.in"
                                        data-testid="login-email-input"
                                        {...register("email", { required: true })}
                                    />
                                    {errors.email && (
                                        <p className="mt-1 text-xs text-rose-600">
                                            Email is required
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        data-testid="login-password-input"
                                        {...register("password", { required: true })}
                                    />
                                    {errors.password && (
                                        <p className="mt-1 text-xs text-rose-600">
                                            Password is required
                                        </p>
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full nest-gradient text-white hover:opacity-95"
                                    data-testid="login-submit-btn"
                                >
                                    <LogIn className="mr-2" size={16} />
                                    {isSubmitting ? "Signing in…" : "Sign in"}
                                </Button>
                            </form>
                            <p className="text-center text-xs text-neutral-500">
                                For franchise team, staff, supervisor, accounts &amp; super admin.
                            </p>
                        </TabsContent>

                        <TabsContent value="shop" className="mt-6 space-y-4">
                            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
                                Shop owners sign in with mobile + password, or use Google.
                            </div>
                            <Link to="/merchant/login" className="block">
                                <Button
                                    className="w-full nest-gradient py-6 text-base font-semibold text-white"
                                    data-testid="shop-mobile-signin-btn"
                                >
                                    <Store className="mr-2" size={18} />
                                    Sign in with mobile
                                </Button>
                            </Link>
                            <div className="relative py-2">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-neutral-200" />
                                </div>
                                <div className="relative flex justify-center text-[11px] uppercase tracking-wide text-neutral-400">
                                    <span className="bg-white px-2">or</span>
                                </div>
                            </div>
                            <Button
                                onClick={startGoogle}
                                variant="outline"
                                className="w-full border-2 border-neutral-300 py-6 text-base font-semibold hover:bg-neutral-50"
                                data-testid="google-signin-btn"
                            >
                                <img
                                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                    alt=""
                                    className="mr-3 h-5 w-5"
                                />
                                Continue with Google
                                <ArrowRight className="ml-auto" size={16} />
                            </Button>
                            <p className="text-center text-xs text-neutral-500">
                                New to OfferNest?{" "}
                                <Link
                                    to="/merchant/register"
                                    className="font-semibold text-orange-600"
                                    data-testid="shop-register-link"
                                >
                                    Create a shop account
                                </Link>
                            </p>
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
        </div>
    );
}
